/**
 * Summary Tab - PDF viewer and JSON extractions
 */

import { Box, Typography, Paper, Alert, Link } from '@mui/material';
import { LessonV1_SpineTagsPrediction } from '../../types';
import { CollapsibleJsonViewer } from '../shared';

interface SummaryTabProps {
  prediction: LessonV1_SpineTagsPrediction;
}

export default function SummaryTab({ prediction }: SummaryTabProps) {
  const PDF_CDN_URL = import.meta.env.VITE_PDF_CDN_URL || 'https://d2ze058wumaaie.cloudfront.net';
  const pdfUrl = prediction.lesson_pdf ? `${PDF_CDN_URL}/${prediction.lesson_pdf}` : null;

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 3
    }}>
      {/* Left column: PDF Extractions */}
      <Box>
        {prediction.lesson_pdf_extractions ? (
          <CollapsibleJsonViewer 
            data={prediction.lesson_pdf_extractions} 
            title="PDF Extractions"
            maxHeight="720px"
            defaultExpandTopLevel={false}
          />
        ) : (
          <Alert severity="warning">
            This prediction doesn't have PDF summary data. The manifest was uploaded but perhaps the prediction hasn't been processed yet.
          </Alert>
        )}
      </Box>

      {/* Right column: PDF Viewer */}
      <Box>
        {pdfUrl ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              PDF Document
            </Typography>
            <Link href={pdfUrl} target="_blank" rel="noopener" sx={{ mb: 2, display: 'block', color: 'primary.light' }}>
              Open PDF in new tab: {prediction.lesson_pdf}
            </Link>
            
            {/* PDF iframe viewer */}
            <Box
              sx={{
                width: '100%',
                height: '700px',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <iframe
                src={`${pdfUrl}#toolbar=1`}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="PDF Viewer"
              />
            </Box>
          </Paper>
        ) : (
          <Alert severity="info">No PDF file available for this lesson.</Alert>
        )}
      </Box>
    </Box>
  );
}